'use client'

import { useEffect, useRef } from 'react'

export default function CustomCursor() {
  const ringRef = useRef<HTMLDivElement>(null)
  const dotRef = useRef<HTMLDivElement>(null)
  const labelRef = useRef<HTMLSpanElement>(null)
  const pos = useRef({ x: 0, y: 0 })
  const ringPos = useRef({ x: 0, y: 0 })
  const rafRef = useRef<number>(0)

  useEffect(() => {
    // Don't render on touch devices
    if ('ontouchstart' in window) return

    const ring = ringRef.current
    const dot = dotRef.current
    if (!ring || !dot) return

    const onMouseMove = (e: MouseEvent) => {
      pos.current = { x: e.clientX, y: e.clientY }
      // dot follows immediately
      dot.style.transform = `translate(${e.clientX - 3}px, ${e.clientY - 3}px)`
    }

    const animateRing = () => {
      // ring follows with lag
      ringPos.current.x += (pos.current.x - ringPos.current.x) * 0.12
      ringPos.current.y += (pos.current.y - ringPos.current.y) * 0.12
      ring.style.transform = `translate(${ringPos.current.x - 20}px, ${ringPos.current.y - 20}px)`
      rafRef.current = requestAnimationFrame(animateRing)
    }
    rafRef.current = requestAnimationFrame(animateRing)

    // Hover states
    const addHoverClass = (e: Event) => {
      const target = e.target as HTMLElement
      if (target.closest('a, button, [data-cursor="pointer"]')) {
        ring.classList.add('ring-expanded')
        dot.style.opacity = '0'
        if (labelRef.current) labelRef.current.style.opacity = '1'
      }
    }

    const removeHoverClass = (e: Event) => {
      const target = e.target as HTMLElement
      if (!target.closest('a, button, [data-cursor="pointer"]')) {
        ring.classList.remove('ring-expanded')
        dot.style.opacity = '1'
        if (labelRef.current) labelRef.current.style.opacity = '0'
      }
    }

    const onMouseEnter = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (target.closest('img, [data-cursor="drag"]')) {
        if (labelRef.current) labelRef.current.textContent = 'DRAG'
      } else {
        if (labelRef.current) labelRef.current.textContent = 'VIEW'
      }
    }

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseover', addHoverClass)
    document.addEventListener('mouseout', removeHoverClass)
    document.addEventListener('mouseover', onMouseEnter)

    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseover', addHoverClass)
      document.removeEventListener('mouseout', removeHoverClass)
      document.removeEventListener('mouseover', onMouseEnter)
      cancelAnimationFrame(rafRef.current)
    }
  }, [])

  return (
    <>
      {/* Outer ring */}
      <div
        ref={ringRef}
        className="cursor-ring"
        aria-hidden="true"
        style={{
          width: 40,
          height: 40,
          border: '1px solid rgba(200, 169, 110, 0.6)',
          position: 'fixed',
          top: 0,
          left: 0,
          borderRadius: '50%',
          pointerEvents: 'none',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'width 0.3s ease, height 0.3s ease, border-color 0.3s ease',
        }}
      >
        <span
          ref={labelRef}
          style={{
            fontFamily: 'var(--font-space-mono, "Space Mono", monospace)',
            fontSize: '8px',
            letterSpacing: '0.1em',
            color: '#C8A96E',
            opacity: 0,
            transition: 'opacity 0.2s ease',
          }}
        >
          VIEW
        </span>
      </div>

      {/* Inner dot */}
      <div
        ref={dotRef}
        className="cursor-dot"
        aria-hidden="true"
        style={{
          width: 6,
          height: 6,
          background: '#C8A96E',
          position: 'fixed',
          top: 0,
          left: 0,
          borderRadius: '50%',
          pointerEvents: 'none',
          zIndex: 10000,
          transition: 'opacity 0.2s ease',
        }}
      />

      <style>{`
        .ring-expanded {
          width: 60px !important;
          height: 60px !important;
          border-color: rgba(200, 169, 110, 0.8) !important;
        }
      `}</style>
    </>
  )
}
